import { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { api, naira, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NIGERIAN_STATES } from "@/lib/constants";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { ClaimVoucher } from "@/components/ClaimVoucher";
import { AdminDrilldown } from "@/components/AdminDrilldown";
import { AdminProgrammes } from "@/components/AdminProgrammes";
import { AdminDataTools } from "@/components/AdminDataTools";
import { AdminImpactStories } from "@/components/AdminImpactStories";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, Users, Wallet, Store, TrendingUp, PackageCheck, MapPin, Building2,
  Activity, AlertTriangle, MessageSquare, RotateCcw, ShieldCheck, Printer, ChevronRight, Landmark, UserCog, FileText,
} from "lucide-react";
import { toast } from "sonner";

function Metric({ icon: Icon, label, value, accent, sub, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="group text-left rounded-2xl border border-emerald-100 bg-white p-5 hover:shadow-md hover:border-emerald-300 transition-all"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 font-medium">{label}</span>
        <Icon className={`w-4 h-4 ${accent || "text-emerald-500"}`} />
      </div>
      <div className="mt-2 font-bold text-2xl text-emerald-950 tabular-nums">{value}</div>
      <div className="mt-1 flex items-center justify-between">
        <span className="text-[11px] text-gray-500">{sub || ""}</span>
        <ChevronRight className="w-3.5 h-3.5 text-emerald-300 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
      </div>
    </button>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [m, setM] = useState(null);
  const [txns, setTxns] = useState([]);
  const [logs, setLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [section, setSection] = useState(null);
  const [reverseTxn, setReverseTxn] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  const [reverseBusy, setReverseBusy] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [sf, setSf] = useState({ name: "", email: "", password: "", role: "state_coordinator", state: "" });

  const loadAll = useCallback(() => {
    api.get("/admin/dashboard").then((r) => setM(r.data.metrics)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/transactions").then((r) => setTxns(r.data.transactions)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/audit-logs").then((r) => setLogs(r.data.logs)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/redemption-analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const loadStaff = () => api.get("/admin/staff").then((r) => setStaff(r.data.staff)).catch(() => {});
  const createStaff = async () => {
    if (!sf.name || !sf.email || !sf.password) return toast.error("Name, email and password are required.");
    if (sf.role === "state_coordinator" && !sf.state) return toast.error("Select a state for the coordinator.");
    try {
      await api.post("/admin/staff", sf);
      toast.success("Staff account created");
      setSf({ name: "", email: "", password: "", role: "state_coordinator", state: "" });
      loadStaff();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleStaff = async (u) => {
    try { await api.patch(`/admin/staff/${u.id}`, { active: u.active === false }); toast.success(u.active === false ? "Reactivated" : "Deactivated"); loadStaff(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reassignStaff = async (u, state) => {
    try { await api.patch(`/admin/staff/${u.id}`, { state }); toast.success(`Reassigned to ${state}`); loadStaff(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const reverse = (txnId) => { setReverseReason(""); setReverseTxn(txnId); };
  const submitReverse = async () => {
    if (reverseReason.trim().length < 3) return toast.error("Please enter a reason (min 3 characters).");
    setReverseBusy(true);
    try { await api.post(`/redemptions/${reverseTxn}/reverse`, { reason: reverseReason.trim() }); toast.success("Transaction reversed — balance restored"); setReverseTxn(null); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setReverseBusy(false); }
  };
  const openClaimVoucher = async (tmfId) => {
    try { const { data } = await api.get(`/beneficiaries/${tmfId}/claim-voucher`); setVoucher(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!m) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;

  const statusColor = (s) => ({ approved: "bg-emerald-100 text-emerald-800", reversed: "bg-blue-100 text-blue-800", flagged: "bg-amber-100 text-amber-800", failed: "bg-red-100 text-red-800" }[s] || "bg-gray-100 text-gray-700");
  const cards = [
    { icon: Users, label: "Registered Beneficiaries", value: m.registered_beneficiaries.toLocaleString(), section: "beneficiaries", testid: "kpi-beneficiaries" },
    { icon: PackageCheck, label: "Active Benefits", value: m.active_benefits.toLocaleString(), section: "active-benefits", testid: "kpi-active-benefits" },
    { icon: Store, label: "Active Vendors", value: m.active_vendors.toLocaleString(), section: "vendors", testid: "kpi-vendors" },
    { icon: Wallet, label: "Total Allocation", value: naira(m.total_allocation), sub: `Funded: ${naira(m.funds_received)}`, section: "allocation", testid: "kpi-allocation" },
    { icon: TrendingUp, label: "Total Redeemed", value: naira(m.total_redeemed), sub: `Food ${naira(m.food_redeemed)} · Cash ${naira(m.cashback_redeemed)}`, accent: "text-red-500", section: "redeemed", testid: "kpi-redeemed" },
    { icon: Landmark, label: "Remaining Balance", value: naira(m.remaining_balance), section: "remaining", testid: "kpi-remaining" },
    { icon: MapPin, label: "States Covered", value: m.states_covered, section: "geographic", testid: "kpi-states" },
    { icon: Building2, label: "Sponsors", value: m.sponsors, section: "sponsors", testid: "kpi-sponsors" },
    { icon: PackageCheck, label: "Active Programmes", value: m.active_programmes, section: "programmes", testid: "kpi-programmes" },
    { icon: Activity, label: "Transactions Today", value: m.transactions_today, section: "today", testid: "kpi-today" },
    { icon: AlertTriangle, label: "Pending Settlements", value: naira(m.pending_settlements), accent: "text-amber-500", section: "settlements", testid: "kpi-settlements" },
    { icon: RotateCcw, label: "Reversed", value: m.reversed_transactions, accent: "text-blue-500", section: "reversed", testid: "kpi-reversed" },
  ];

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">TMF National Command Centre</span>
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="mt-2 font-display font-bold text-3xl text-emerald-950">National Dashboard</h1>
            <p className="mt-1 text-sm text-gray-500">Every card is live — click any indicator to open its management module.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button data-testid="admin-sms-btn" variant="outline" className="border-emerald-200 text-emerald-800" onClick={() => window.location.assign("/admin/sms")}>
              <MessageSquare className="w-4 h-4 mr-2" /> SMS
            </Button>
            <Button data-testid="admin-payouts-btn" variant="outline" className="border-emerald-200 text-emerald-800" onClick={() => window.location.assign("/admin/payouts")}>
              <Wallet className="w-4 h-4 mr-2" /> Payouts
            </Button>
            <Button data-testid="admin-invoices-btn" variant="outline" className="border-emerald-200 text-emerald-800" onClick={() => window.location.assign("/admin/invoices")}>
              <FileText className="w-4 h-4 mr-2" /> Invoices
            </Button>
            <Button data-testid="admin-complaints-btn" variant="outline" className="border-amber-200 text-amber-800" onClick={() => window.location.assign("/admin/complaints")}>
              <AlertTriangle className="w-4 h-4 mr-2" /> Complaints
            </Button>
            <Button data-testid="manage-staff-btn" variant="outline" className="border-emerald-200 text-emerald-800" onClick={() => { setStaffOpen(true); loadStaff(); }}>
              <UserCog className="w-4 h-4 mr-2" /> Manage Staff
            </Button>
            <AdminDataTools isSuper={isSuper} />
          </div>
        </div>

        <div className="mt-8 grid gap-4 grid-cols-2 lg:grid-cols-4">
          {cards.map((c) => (
            <Metric key={c.label} {...c} onClick={() => setSection(c.section)} />
          ))}
        </div>

        {/* Secondary visuals */}
        <div className="mt-8 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 rounded-2xl border border-emerald-100 bg-white p-6">
            <h3 className="font-display font-semibold text-lg text-emerald-950">Redemption Trend</h3>
            {analytics?.trend?.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={analytics.trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => naira(v)} />
                  <Area type="monotone" dataKey="amount" stroke="#059669" strokeWidth={2} fill="url(#g)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">No redemption activity yet.</div>}
          </div>
          <div className="lg:col-span-2 rounded-2xl border border-emerald-100 bg-white p-6">
            <h3 className="font-display font-semibold text-lg text-emerald-950 mb-3">Recent Transactions</h3>
            <div className="space-y-3 max-h-[220px] overflow-auto">
              {txns.length === 0 && <p className="text-sm text-gray-400">No transactions yet.</p>}
              {txns.slice(0, 12).map((t) => (
                <div key={t.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
                  <div><div className="font-mono text-[11px] text-gray-400">{t.txn_id}</div><div className="text-xs">{t.tmf_id}</div></div>
                  <div className="text-right"><div className={`font-semibold ${t.type === "reversal" ? "text-blue-600" : "text-emerald-700"}`}>{naira(t.amount)}</div><Badge className={statusColor(t.status)}>{t.status}</Badge></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {m && m.awaiting_activation > 0 && (
          <div data-testid="awaiting-activation-banner" className="mt-8 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
            <span data-testid="awaiting-activation-count" className="inline-flex items-center justify-center min-w-8 h-8 px-2 rounded-full bg-amber-500 text-white font-bold text-sm">{m.awaiting_activation}</span>
            <div className="text-sm text-amber-900">
              <span className="font-semibold">{m.awaiting_activation} programme{m.awaiting_activation > 1 ? "s" : ""} awaiting activation.</span>{" "}
              Confirm funding in Programme Financials below to switch {m.awaiting_activation > 1 ? "them" : "it"} on and release benefits.
            </div>
          </div>
        )}

        <AdminProgrammes metrics={m} onChanged={loadAll} />

        <AdminImpactStories />

        {/* Full transaction ledger */}
        <h2 className="mt-10 font-display font-bold text-xl text-emerald-950">National Transaction Ledger</h2>
        <div className="mt-4 rounded-2xl border border-emerald-100 bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-emerald-50/60">
              <TableHead>Txn ID</TableHead><TableHead>Type</TableHead><TableHead>TMF ID</TableHead><TableHead>Vendor</TableHead>
              <TableHead>Component</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {txns.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-10">No transactions yet.</TableCell></TableRow>}
              {txns.slice(0, 60).map((t) => (
                <TableRow key={t.id} data-testid={`admin-txn-${t.txn_id}`}>
                  <TableCell className="font-mono text-xs">{t.txn_id}</TableCell>
                  <TableCell className="capitalize">{t.type}</TableCell>
                  <TableCell className="font-mono text-xs">{t.tmf_id}</TableCell>
                  <TableCell className="text-xs">{t.vendor_name}</TableCell>
                  <TableCell className="text-xs">{t.component || "—"}</TableCell>
                  <TableCell className="font-semibold">{naira(t.amount)}</TableCell>
                  <TableCell><Badge className={statusColor(t.status)}>{t.status}</Badge></TableCell>
                  <TableCell>
                    {t.type === "redemption" && t.status === "approved" && t.settlement_status !== "settled" && <Button data-testid={`reverse-${t.txn_id}`} size="sm" variant="outline" onClick={() => reverse(t.txn_id)}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reverse</Button>}
                    {t.type === "redemption" && t.settlement_status === "settled" && <Badge className="bg-gray-200 text-gray-700">settled</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Audit log */}
        <h2 className="mt-10 font-display font-bold text-xl text-emerald-950">Audit Log</h2>
        <div className="mt-4 rounded-2xl border border-emerald-100 bg-white divide-y divide-gray-100 max-h-96 overflow-auto">
          {logs.length === 0 && <p className="text-center text-gray-500 py-10">No audit entries yet.</p>}
          {logs.map((l) => (
            <div key={l.id} className="p-4 flex items-start justify-between text-sm">
              <div>
                <span className="font-semibold text-emerald-900">{l.who_name}</span> <span className="text-gray-600">{l.action}</span>
                {l.details && <span className="text-gray-400"> — {l.details}</span>}
              </div>
              <span className="text-xs text-gray-400 font-mono whitespace-nowrap ml-4">{new Date(l.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center gap-2 text-sm text-gray-500">
          <MessageSquare className="w-4 h-4 text-emerald-500" /> {m.sms_sent.toLocaleString()} SMS dispatched (DEMO engine)
        </div>

        <AdminDrilldown section={section} onClose={() => setSection(null)} onChanged={loadAll} onClaimVoucher={openClaimVoucher} onReverse={reverse} />

        <Dialog open={!!reverseTxn} onOpenChange={(o) => !o && setReverseTxn(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Reverse Transaction</DialogTitle></DialogHeader>
            <p className="text-sm text-gray-600">Reversing <span className="font-mono text-emerald-700">{reverseTxn}</span> restores the beneficiary balance and creates an auditable reversal record. This cannot be undone.</p>
            <div className="mt-2">
              <Label>Reason for reversal <span className="text-red-500">*</span></Label>
              <Textarea data-testid="reverse-reason" value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} placeholder="e.g. Vendor entered the wrong amount" rows={3} className="mt-1.5" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReverseTxn(null)}>Cancel</Button>
              <Button data-testid="reverse-confirm" onClick={submitReverse} disabled={reverseBusy} className="bg-emerald-600 hover:bg-emerald-700">
                {reverseBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4 mr-2" />Confirm Reversal</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={staffOpen} onOpenChange={setStaffOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Staff Management</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input data-testid="staff-name" value={sf.name} onChange={(e) => setSf({ ...sf, name: e.target.value })} className="mt-1.5 h-10" /></div>
                <div><Label>Email</Label><Input data-testid="staff-email" type="email" value={sf.email} onChange={(e) => setSf({ ...sf, email: e.target.value })} className="mt-1.5 h-10" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Password</Label><Input data-testid="staff-password" type="password" value={sf.password} onChange={(e) => setSf({ ...sf, password: e.target.value })} className="mt-1.5 h-10" /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={sf.role} onValueChange={(v) => setSf({ ...sf, role: v })}>
                    <SelectTrigger data-testid="staff-role" className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="state_coordinator">State Coordinator</SelectItem><SelectItem value="finance">Finance Officer</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>State</Label>
                  <Select value={sf.state} onValueChange={(v) => setSf({ ...sf, state: v })} disabled={sf.role !== "state_coordinator"}>
                    <SelectTrigger data-testid="staff-state" className="mt-1.5 h-10"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{NIGERIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="staff-create" onClick={createStaff} className="w-full bg-emerald-600 hover:bg-emerald-700">Create Staff Account</Button>
              <div className="rounded-xl border border-emerald-100 divide-y divide-gray-100 max-h-56 overflow-auto">
                {staff.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No coordinator/finance staff yet.</p>}
                {staff.map((u) => (
                  <div key={u.id} data-testid={`staff-row-${u.email}`} className="flex items-center justify-between p-3 text-sm gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{u.name} {u.active === false && <span className="text-[10px] text-gray-400">(inactive)</span>}</div>
                      <div className="text-xs text-gray-500 truncate">{u.email}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className="bg-emerald-100 text-emerald-800">{u.role === "finance" ? "Finance" : `Coord · ${u.state}`}</Badge>
                      {u.role === "state_coordinator" && (
                        <Select value="" onValueChange={(v) => reassignStaff(u, v)}>
                          <SelectTrigger data-testid={`staff-reassign-${u.email}`} className="h-8 w-28 text-xs"><SelectValue placeholder="Reassign" /></SelectTrigger>
                          <SelectContent>{NIGERIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                      <Button data-testid={`staff-toggle-${u.email}`} size="sm" variant="outline" onClick={() => toggleStaff(u)}>{u.active === false ? "Activate" : "Deactivate"}</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!voucher} onOpenChange={(o) => !o && setVoucher(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>TMF-ID Claim Voucher</DialogTitle></DialogHeader>
            {voucher && <ClaimVoucher data={voucher} />}
            <div className="flex justify-end gap-2 no-print">
              <Button variant="outline" onClick={() => setVoucher(null)}>Close</Button>
              <Button data-testid="print-claim-voucher" onClick={() => window.print()} className="bg-emerald-600 hover:bg-emerald-700"><Printer className="w-4 h-4 mr-2" /> Print</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
